import {
  ABSA_BY_ID,
  ANOMALY_BY_ID,
  CATALOG,
  demoEmbedding,
  FORECAST_BY_ID,
  PMF_BY_ID,
  PMF_SCORES,
  SALES_BY_ID,
} from './engine/pmfEngine.js';

function J(obj) {
  return JSON.stringify(Array.isArray(obj) ? obj.map((row) => ({ demo: true, ...row })) : { demo: true, ...obj }, null, 2);
}

const P1 = CATALOG.products[0];
const A1 = ABSA_BY_ID[P1.product_id];
const F1 = FORECAST_BY_ID[P1.product_id];
const N1 = ANOMALY_BY_ID[P1.product_id];
const S1 = PMF_BY_ID[P1.product_id];
const H1 = SALES_BY_ID[P1.product_id];
const CAUSE_KO = { orders: '주문', stock: '재고', ctr: 'CTR', roas: 'ROAS', cvr: '전환', price: '가격' };

export const DECKS = {
  "overview": {
    "id": "overview",
    "title": "System Map",
    "subtitle": "과제 1~5 · 전체 구조",
    "steps": [
      {
        "code": "ID",
        "en": "END-TO-END MAP",
        "title": "원천 → 패브릭 → 3모델 → PMF",
        "shape": "한 파이프 · 5과제 연결도",
        "hero": true,
        "bullets": [
          "한 카탈로그 4 SKU가 product_id로 전 모델을 관통",
          "패브릭이 표준 자산을 만들고 과제 2~4가 병렬 소비",
          "PMF는 모델 출력을 가중합해 모의 시장 적합성을 계산"
        ],
        "jsonTitle": "시스템 정의",
        "json": J({
          join: ['product_id', 'sku'],
          sources: ['web_product', 'review_cs', 'public_data', 'event_log'],
          fabric: '표준 봉투 · Kafka · 벡터 · API',
          models: { xai: '감성·요인', forecast: 'p10/p50/p90', anomaly: '이상점수' },
          pmf: S1.formula,
        })
      },
      {
        "code": "SRC",
        "en": "SOURCES · 원천 4종",
        "title": "이종 원천 · 같은 조인키",
        "shape": "웹 · 리뷰/CS · 공공 · 이벤트 + product_id 축",
        "bullets": [
          "웹 상품 JSON · 리뷰·CS 텍스트 · CPI·계절·검색 · click·purchase",
          "이 데모는 상품·고객·시장·이벤트 데이터를 사용",
          "상품 데이터는 product_id / sku, 공공 지표는 기간으로 연결"
        ],
        "jsonTitle": "원천 스케치",
        "json": J({
          product_id: P1.product_id,
          sku: P1.sku,
          web_product: { product_name: P1.product_name, price: P1.price, stock: P1.stock },
          review_cs: { reviews: P1.reviews.length, cs: P1.cs.length },
          public_data: { ...CATALOG.market, search: P1.search_trend },
          event_log: { purchase: P1.ad_metrics.purchase },
        })
      },
      {
        "code": "FAB",
        "en": "FABRIC · 표준화",
        "title": "원본 → 표준 자산",
        "shape": "수집 · 허브 · 가공 · 저장 · API 5층",
        "bullets": [
          "Layer1 표준 봉투 · Layer2 Kafka 토픽 4레인",
          "Layer3 특징·임베딩 · Layer4 Redis/S3/Milvus",
          "과제 2~5는 이 자산만 읽는다"
        ],
        "map": [
          { from: "web_product", to: "product_signal" },
          { from: "review_cs", to: "review_stream" },
          { from: "public_data", to: "market_stream" },
          { from: "event_log", to: "sale_event" }
        ]
      },
      {
        "code": "MOD",
        "en": "MODELS · 과제 2·3·4",
        "title": "같은 자산 · 다른 질문",
        "shape": "XAI 막대 · 수요 파동 · 이상 산포",
        "bullets": [
          "XAI — 사전 기반 감성 · 수동 가중 기여",
          "수요예측 — 시계열 → p10 / p50 / p90 · 발주",
          "이상탐지 — 주문·재고·CTR·ROAS·CVR·가격 잔차"
        ],
        "grid": [
          { n: "02", name: "XAI", desc: `감성 ${A1.absa_mean} · ${A1.aspects.map((a) => a.name).join(' · ')}` },
          { n: "03", name: "수요예측", desc: `p50 ${F1.p50} · 발주 ${F1.order} · ${F1.status}` },
          { n: "04", name: "이상탐지", desc: `점수 ${N1.anomaly_score} · ${N1.state}` },
          { n: "KEY", name: P1.product_id, desc: `${P1.product_name} 한 레코드를 세 모델이 읽음` }
        ]
      },
      {
        "code": "JOIN",
        "en": "JOIN · 합류",
        "title": "세 갈래가 한 SKU로",
        "shape": "3레인 → product_id 깔때기",
        "bullets": [
          "모델마다 JSON 봉투는 같아도 페이로드는 다름",
          "XAI=요인, 수요=분위예측, 이상=점수·알림",
          "합류 키는 product_id · sku 뿐"
        ],
        "jsonTitle": "조인 레코드",
        "json": J({
          product_id: P1.product_id,
          sku: P1.sku,
          xai: { absa_mean: A1.absa_mean, aspects: A1.aspects },
          forecast: { p10: F1.p10, p50: F1.p50, p90: F1.p90, status: F1.status },
          anomaly: { score: N1.anomaly_score, state: N1.state, causes: N1.causes },
        })
      },
      {
        "code": "PMF",
        "en": "FUSION · 가중합",
        "title": "시장에서 얼마나 먹히나",
        "shape": "가중 위성 4개 + 점수 핵",
        "bullets": [
          "시장성과 0.30 · XAI 0.30 · 수요 0.25 · 운영 0.15",
          "기여분 합 = PMF 0~100",
          "판정 강함 75+ · 양호 60+ · 주의 45+ · 약함"
        ],
        "jsonTitle": "융합식",
        "json": J({
          product_id: S1.product_id,
          formula: S1.formula,
          parts: S1.parts.map((p) => ({ label: p.label, score: p.score, weight: p.weight, contribution: p.contribution })),
          pmf_score: S1.pmf_score,
          verdict: S1.verdict,
        }),
        "metricLabel": "PMF · 시장 적합성",
        "metric": `${S1.pmf_score} · ${S1.verdict}`
      },
      {
        "code": "SKU",
        "en": "CATALOG · 4상품",
        "title": "같은 식 · 갈라진 점수",
        "shape": "PMF 높이 = 시장 적합성",
        "bullets": [
          "세럼 양호 > 토너 양호 > 레티놀 주의 > 선크림 주의",
          "토너는 광고가 세지만 운영 주의가 깎음",
          "선크림은 제철인데도 이 SKU만 안 팔림"
        ],
        "table": PMF_SCORES.map((s, i) => ({
          stage: `#${i + 1} ${s.product_name}`,
          shape: `${s.pmf_score} · ${s.verdict} · 약점 ${s.weakest.label} ${s.weakest.score}`,
        }))
      },
      {
        "code": "SUM",
        "en": "SUMMARY · 전체 구조",
        "title": "다섯 과제가 한 시스템",
        "shape": "원천 · 패브릭 · XAI · 수요 · 이상 · PMF",
        "bullets": [
          "앞 탭은 모델 내부, 이 탭은 연결",
          "최종 산출은 PMF 점수 + 왜 이 점수인가"
        ],
        "metricLabel": "관통 카탈로그",
        "metric": "4 SKU · 1 파이프",
        "table": [
          { stage: "원천", shape: "웹 JSON · 리뷰 텍스트 · 공공 지표 · 이벤트" },
          { stage: "01 패브릭", shape: "표준 봉투 · Kafka · 벡터 · REST" },
          { stage: "02 XAI", shape: "속성별 감성 · 가중 기여" },
          { stage: "03 수요예측", shape: "p10 / p50 / p90 · 발주" },
          { stage: "04 이상탐지", shape: "Anomaly Score · 알림" },
          { stage: "05 PMF", shape: S1.formula },
        ]
      }
    ]
  },
  "fabric": {
    "id": "fabric",
    "title": "Hybrid Data Fabric",
    "subtitle": "과제 1 · Architecture",
    "steps": [
      {
        "code": "ID",
        "en": "HYBRID DATA FABRIC",
        "title": "하이브리드 데이터 패브릭",
        "shape": "과제 1 · 이종 원천 표준화",
        "hero": true,
        "bullets": [
          "역할 — 웹·리뷰·공공·이벤트 → 표준 자산",
          "구조 — Layer 1~5 수집·허브·가공·저장·API",
          "산출 — 정제 데이터 · Kafka · 벡터 · REST",
          "연계 — 과제 2~5 공통 입력"
        ],
        "jsonTitle": "모델 정의",
        "json": "{\n  \"model\": \"Hybrid Data Fabric\",\n  \"task\": 1,\n  \"role\": \"이종 원천 → 표준 자산\",\n  \"layers\": [\"collect\", \"hub\", \"process\", \"store\", \"api\"],\n  \"feeds\": [\"xai\", \"forecast\", \"anomaly\", \"pmf\"]\n}"
      },
      {
        "code": "IN",
        "en": "INPUT · 원천 데이터",
        "title": "원천 4종 · 이종 포맷",
        "shape": "원본 4갈래 · 크기·방향 비균질",
        "bullets": [
          "웹·모바일 상품 — JSON API",
          "리뷰·CS — 비정형 텍스트",
          "공공·검색 — CPI · 계절지수 · 검색관심도",
          "이벤트·로그 — click · purchase"
        ],
        "jsonTitle": "입력 예시 · 웹 상품 API 원본",
        "json": J({ product_id: P1.product_id, sku: P1.sku, product_name: P1.product_name, price: P1.price, stock: P1.stock, channel: P1.channel })
      },
      {
        "code": "L1",
        "en": "LAYER 1 · 데이터 수집",
        "title": "원본 → 표준 봉투",
        "shape": "이종 레코드 · 동일 격자 정렬",
        "bullets": [
          "수집: POST API · 스케줄러 · 병렬",
          "변환: JSON · 리뷰 텍스트 · 공공 지표 · 이벤트 → 표준 dict",
          "설계 목표: 수작업 3시간 → 10~30분 · 실측 전"
        ],
        "metricLabel": "수집 소요 시간 목표",
        "metric": "3시간 → 10~30분",
        "jsonTitle": "Layer1 출력 포맷 · 표준 봉투",
        "json": J({ source: 'web_product', collected_at: `${CATALOG.as_of}T03:44:12Z`, record_id: `${P1.product_id}-web`, payload: { product_id: P1.product_id, sku: P1.sku, product_name: P1.product_name, price: P1.price, stock: P1.stock } })
      },
      {
        "code": "L2",
        "en": "LAYER 2 · 메시지 허브",
        "title": "표준 봉투 → Kafka 토픽",
        "shape": "토픽 레인 4개 · 메시지 흐름",
        "bullets": [
          "설계: 스키마 검증 · 비정상 원본과 실패 사유 보존",
          "record_id — 멱등 키 · 중복 차단",
          "소스별 토픽 자동 라우팅"
        ],
        "map": [
          {
            "from": "web_product",
            "to": "product_signal"
          },
          {
            "from": "review_cs",
            "to": "review_stream"
          },
          {
            "from": "public_data",
            "to": "market_stream"
          },
          {
            "from": "event_log",
            "to": "sale_event"
          }
        ]
      },
      {
        "code": "L3",
        "en": "LAYER 3 · 처리 · AI 가공",
        "title": "정제 · 특징 · 임베딩",
        "shape": "레코드 확장 · 임베딩 점구름",
        "bullets": [
          "품질 검토·격리 흐름은 별도 품질 실습에서 탐색",
          "특징: 가격 등급 · 리뷰 감성 · 검색 트렌드",
          "데모: 모의 3차원 벡터 · 상품 설명 텍스트",
          "산출: 표준 자산의 예시 형식"
        ],
        "jsonTitle": "Layer3에서 새로 붙는 필드",
        "json": J({ record_id: `${P1.product_id}-web`, features: { price_tier: 'mid', sentiment_score: A1.channel_score, stock: P1.stock }, embedding: demoEmbedding(P1), embedding_model: 'synthetic_3d_demo', llm_context: `[상품] ${P1.product_name}` })
      },
      {
        "code": "L4",
        "en": "LAYER 4 · 저장소 · 색인",
        "title": "Hot · Cold · Vector 3중 저장",
        "shape": "Redis 링 · S3 슬랩 · Milvus 셸",
        "bullets": [
          "Glue 카탈로그 — 위치 · 파티션 메타"
        ],
        "stores": [
          {
            "path": "HOT PATH",
            "name": "Redis",
            "desc": "최근 레코드 캐시 · 저지연 조회"
          },
          {
            "path": "COLD PATH",
            "name": "Amazon S3",
            "desc": "원본·가공본 zone 파티션 보관"
          },
          {
            "path": "VECTOR STORE",
            "name": "Milvus",
            "desc": "임베딩 색인 · 유사도 검색"
          }
        ]
      },
      {
        "code": "L5",
        "en": "LAYER 5 · 검색 · 활용연계",
        "title": "산출 API 5종",
        "shape": "검색 · 모니터 · 추천 · 알림 · 리포트",
        "bullets": [],
        "apis": [
          {
            "n": "01",
            "name": "벡터 검색 API",
            "desc": "유사 상품 Top-k"
          },
          {
            "n": "02",
            "name": "실시간 모니터링 API",
            "desc": "캐시·저장 현황"
          },
          {
            "n": "03",
            "name": "추천 / 분석 연계 API",
            "desc": "연관 상품 추천"
          },
          {
            "n": "04",
            "name": "알림 / 이상탐지 연계 API",
            "desc": "과제4 탐지 수신"
          },
          {
            "n": "05",
            "name": "BI / 리포트 연계 API",
            "desc": "누적 데이터 요약"
          }
        ],
        "jsonTitle": "추천 API 모의 응답 · PMF 점수 기준",
        "json": J([{ record_id: S1.product_id, score: Math.round((S1.pmf_score / 100) * 1000) / 1000, score_kind: 'normalized_demo_pmf', product_name: S1.product_name, sku: S1.sku }])
      },
      {
        "code": "OUT",
        "en": "OUTPUT · 산출물",
        "title": "표준 자산 4종",
        "shape": "정제 · 토픽 · 벡터 · API",
        "bullets": [
          "과제2~5 공통 입력"
        ],
        "grid": [
          {
            "n": "OUT 01",
            "name": "정제 데이터",
            "desc": "표준 봉투 + 특징이 붙은 레코드"
          },
          {
            "n": "OUT 02",
            "name": "메시지 토픽",
            "desc": "소스별로 라우팅된 Kafka 스트림"
          },
          {
            "n": "OUT 03",
            "name": "벡터 인덱스",
            "desc": "Milvus에 색인된 임베딩"
          },
          {
            "n": "OUT 04",
            "name": "표준화 API",
            "desc": "다섯 갈래 REST 엔드포인트"
          }
        ]
      },
      {
        "code": "SUM",
        "en": "SUMMARY · 데이터 형태 변화",
        "title": "5단계 · 자동화 목표 90%",
        "shape": "입력 → 수집 → 허브 → 가공 → 저장 → API",
        "bullets": [
          "과제2~5용 표준 데이터 공급",
          "다음: 과제2 XAI 엔진 연동"
        ],
        "metricLabel": "자동화율 · 확보량 목표 (실측 전)",
        "metric": "90% · 월 5,000건+",
        "table": [
          {
            "stage": "입력",
            "shape": "소스별 원본 (JSON · 스트림 · 이벤트)"
          },
          {
            "stage": "Layer1 · 수집",
            "shape": "표준 봉투 (JSON)"
          },
          {
            "stage": "Layer2 · 허브",
            "shape": "Kafka 토픽 메시지"
          },
          {
            "stage": "Layer3 · 가공",
            "shape": "특징 + 임베딩 + 프롬프트 포함 레코드"
          },
          {
            "stage": "Layer4 · 저장",
            "shape": "Redis · S3 · Milvus 3중 저장"
          },
          {
            "stage": "Layer5 · 출력",
            "shape": "REST API JSON 응답"
          }
        ]
      }
    ]
  },
  "xai": {
    "id": "xai",
    "title": "XAI PMF Engine",
    "subtitle": "과제 2 · Factor Analysis",
    "steps": [
      {
        "code": "ID",
        "en": "XAI PMF ENGINE",
        "title": "XAI PMF Engine",
        "shape": "과제 2 · 설명가능 요인 분석",
        "hero": true,
        "bullets": [
          "역할 — 리뷰·광고·판매 → 요인 설명",
          "방법 — 감성 사전 · 수동 가중 기여분",
          "산출 — 요인 카드 · 중요도 · 근거 문장",
          "연계 — 패브릭 표준 자산 입력"
        ],
        "jsonTitle": "모델 정의",
        "json": "{\n  \"model\": \"XAI PMF Engine\",\n  \"task\": 2,\n  \"role\": \"리뷰·광고·판매 → 요인 설명\",\n  \"output\": [\"factor_importance\", \"weighted_components\", \"evidence\"]\n}"
      },
      {
        "code": "IN",
        "en": "INPUT · 세 갈래 데이터",
        "title": "입력 3축 · 리뷰·광고·판매",
        "shape": "텍스트 · 지표 · 테이블 3덩어리",
        "bullets": [
          "상품 리뷰·CS — 텍스트 (배송 · 가격 · 품질)",
          "광고 성과 — CTR · CPC · ROAS",
          "판매·재고 — 판매량 · 재고 · 전환율"
        ],
        "jsonTitle": "입력 원천 스케치",
        "json": J({ product_id: P1.product_id, reviews: P1.reviews, cs: P1.cs, ad: P1.ad_metrics, sales: { units_7d: H1.slice(-7).reduce((s, r) => s + r.units, 0), stock: P1.stock, cvr: P1.behavior.cvr } })
      },
      {
        "code": "L1",
        "en": "LAYER 1 · 데이터 통합",
        "title": "리뷰·광고·판매 정합",
        "shape": "3축 → 통합 레코드",
        "bullets": [
          "수집: 리뷰 · CS · 광고 · 판매",
          "데모: 고정 카탈로그의 리뷰·CS·광고·판매 조인",
          "산출: 분석용 Feature"
        ],
        "jsonTitle": "Layer1 출력 · 통합 레코드",
        "json": J({ product_id: P1.product_id, sku: P1.sku, review_text: P1.reviews[0], ad_metrics: P1.ad_metrics, sales: { units_7d: H1.slice(-7).reduce((s, r) => s + r.units, 0), stock: P1.stock } })
      },
      {
        "code": "L2",
        "en": "LAYER 2 · ABSA 분석",
        "title": "속성별 감성 점수",
        "shape": "배송 · 가격 · 품질 · 사용성",
        "bullets": [
          "속성 추출: 배송 · 가격 · 품질 · 사용성",
          "극성: 긍정 · 중립 · 부정 → 속성별 점수",
          "데모: 사전에 등록한 단어의 긍정·부정 점수 평균"
        ],
        "grid": A1.aspects.map((row, i) => ({
          n: `ATTR 0${i + 1}`,
          name: row.name,
          desc: String(row.score),
        })),
      },
      {
        "code": "L3",
        "en": "LAYER 3 · XAI · PMF 분석",
        "title": "가중합 · PMF 기여 요인",
        "shape": "기여도 순위 막대",
        "bullets": [
          "결합: CTR · CPC · ROAS · 전환 · 재구매",
          "가중치 × 채널 점수로 기여분 계산",
          "수동 가중합 예시 · 실제 SHAP 계산은 미구현",
          "산출: 4개 채널의 기여분과 근거"
        ],
        "jsonTitle": "가중 기여 출력",
        "json": J({ product_id: S1.product_id, target: 'pmf_score', explanation_method: S1.explanation_method, weighted_components: S1.parts })
      },
      {
        "code": "L4",
        "en": "LAYER 4 · 전략 인사이트",
        "title": "분석 → 실행 전략",
        "shape": "광고 · 상품/CS · 재고 3클러스터",
        "bullets": [
          "전달: 대시보드 · API"
        ],
        "stores": [
          {
            "path": "AD STRATEGY",
            "name": "광고 전략",
            "desc": "고성과 소재 확대, 저성과 광고 중단"
          },
          {
            "path": "PRODUCT / CS",
            "name": "상품 · CS 대응",
            "desc": "가격 · 품질 개선, 문의 대응 우선순위"
          },
          {
            "path": "INVENTORY",
            "name": "재고 · 재판매",
            "desc": "수요 · 회전 기반 운영 최적화"
          }
        ]
      },
      {
        "code": "ACT",
        "en": "INSIGHT → ACTION",
        "title": "인사이트 → 실행 4갈래",
        "shape": "개선 · 프로모션 · 재고 · 확산",
        "bullets": [],
        "apis": [
          {
            "n": "01",
            "name": "개선 실행 과제 도출",
            "desc": "상품 · 가격 · 채널 · CS 개선 우선순위"
          },
          {
            "n": "02",
            "name": "프로모션 · 추천 자동 제안",
            "desc": "광고 소재 · 타겟 · 전략 자동화"
          },
          {
            "n": "03",
            "name": "재고 · 재판매 전략 최적화",
            "desc": "수요 · 회전 · 성과 기반 운영"
          },
          {
            "n": "04",
            "name": "확산 인사이트",
            "desc": "조직 공유, 지속 개선 모니터링"
          }
        ]
      },
      {
        "code": "OUT",
        "en": "OUTPUT · 산출물",
        "title": "전략 산출물 4종",
        "shape": "리포트 · 대시보드 · API",
        "bullets": [
          "출력: 감성 · XAI · 우선순위 · 전략 API"
        ],
        "grid": [
          {
            "n": "OUT 01",
            "name": "감성 리포트",
            "desc": "속성별 감성 점수"
          },
          {
            "n": "OUT 02",
            "name": "XAI 요인 리포트",
            "desc": "4개 채널의 가중 기여분"
          },
          {
            "n": "OUT 03",
            "name": "우선순위 대시보드",
            "desc": "실행 액션 + 우선순위"
          },
          {
            "n": "OUT 04",
            "name": "전략추천 API",
            "desc": "실행팀이 바로 받는 엔드포인트"
          }
        ]
      },
      {
        "code": "SUM",
        "en": "SUMMARY",
        "title": "PMF-ROAS 일치도 개선 목표",
        "shape": "입력 → 통합 → 감성 사전 → 가중합 → 전략",
        "bullets": [
          "XAI 기반 요인 분석 · 판단 근거 공개",
          "다음: 과제3 수요예측 · 과제5 PMF Score"
        ],
        "metricLabel": "기획 목표 · 검증 데이터 없음",
        "metric": "74% → 90% (목표)",
        "table": [
          {
            "stage": "입력",
            "shape": "리뷰 텍스트 · 광고 지표 · 판매 테이블"
          },
          {
            "stage": "Layer1 · 통합",
            "shape": "통합 레코드 (JSON)"
          },
          {
            "stage": "Layer2 · ABSA",
            "shape": "속성별 감성 점수"
          },
          {
            "stage": "Layer3 · XAI·PMF",
            "shape": "4개 채널의 가중 기여분"
          },
          {
            "stage": "Layer4 · 전략",
            "shape": "실행 액션 + 우선순위"
          },
          {
            "stage": "출력",
            "shape": "대시보드 · 리포트 · 전략 추천 API"
          }
        ]
      }
    ]
  },
  "forecast": {
    "id": "forecast",
    "title": "Demand Forecast",
    "subtitle": "과제 3 · Inventory Optimization",
    "steps": [
      {
        "code": "ID",
        "en": "DEMAND FORECAST",
        "title": "수요예측 엔진",
        "shape": "과제 3 · 재고 최적화",
        "hero": true,
        "bullets": [
          "역할 — 시계열 → p10 / p50 / p90",
          "방법 — 생성 판매 이력의 선형 추세 · 잔차 구간",
          "산출 — 분위 예측 · 재고 액션",
          "연계 — 패브릭 판매·이벤트 스트림"
        ],
        "jsonTitle": "모델 정의",
        "json": "{\n  \"model\": \"Demand Forecast\",\n  \"task\": 3,\n  \"role\": \"시계열 → p10/p50/p90\",\n  \"output\": [\"quantile_forecast\", \"inventory_action\"]\n}"
      },
      {
        "code": "IN",
        "en": "INPUT · 두 축 데이터",
        "title": "입력 2축 · 판매 + 실시간",
        "shape": "시계열 · 외부지표 · 이벤트 스트림",
        "bullets": [
          "내부 판매 — 과거·현재 판매량 시계열",
          "외부 지표 — 프로모션 · 계절 · 공급",
          "실시간 반응 — 조회 · 클릭 · 장바구니"
        ],
        "jsonTitle": "입력 스케치",
        "json": J({ sku: P1.sku, sales_history: H1.slice(-14).map((r) => r.units), seasonal_idx: P1.sales_seed.seasonal, search_index: P1.search_trend.index, live: { views: P1.behavior.views, carts: P1.behavior.carts } })
      },
      {
        "code": "L1",
        "en": "LAYER 1 · 피처 추출",
        "title": "예측용 피처셋",
        "shape": "Look-back · 캘린더 · 정규화",
        "bullets": [
          "Look-back Window — 판매 이력 정합",
          "보조 피처 예시 — 계절·검색 지표 (현재 계산에는 미사용)",
          "현재 계산 — 일별 판매량에 선형 회귀 적용"
        ],
        "jsonTitle": "Layer1 피처셋",
        "json": J({ sku: P1.sku, date: CATALOG.as_of, sales_lag_7: H1.slice(-7).reduce((s, r) => s + r.units, 0), sales_lag_1: H1[H1.length - 1].units, is_holiday: false, seasonal_idx: P1.sales_seed.seasonal, search_index: P1.search_trend.index, promo: P1.sales_seed.promo_day >= 0 })
      },
      {
        "code": "L2",
        "en": "LAYER 2 · 선형 기준선",
        "title": "선형 추세로 수요 추정",
        "shape": "판매 이력 → 선형 추세 → 잔차 구간",
        "bullets": [
          "데모 모델: 선형 회귀 기준선",
          "90일 판매 이력으로 이후 28일 수요 추정",
          "TFT 학습·GPU 추론은 연결하지 않음"
        ],
        "jsonTitle": "모델 설정",
        "json": J({ model: F1.model, interval_method: F1.interval_method, evaluation: F1.evaluation, lookback: F1.lookback, horizon: F1.horizon, quantiles: [0.1, 0.5, 0.9] })
      },
      {
        "code": "L3",
        "en": "LAYER 3 · 확률적 예측",
        "title": "p10 · p50 · p90 구간 예측",
        "shape": "예측 밴드 3중",
        "bullets": [
          "잔차의 정규분포를 가정한 p10 · p50 · p90 근사",
          "선형 추세 중심값 + 미보정 추정 구간",
          "리스크: 안정 · 주의 · 위험"
        ],
        "grid": CATALOG.products.map((p) => {
          const f = FORECAST_BY_ID[p.product_id];
          return { n: p.sku, name: p.product_name, desc: `${f.p50} · ${f.status}` };
        }),
      },
      {
        "code": "L4",
        "en": "LAYER 4 · 재고 최적화",
        "title": "예측 → 재고 액션",
        "shape": "Safety Stock · Reorder · Action",
        "bullets": [
          "전달: 물류 · 운영 자동 권고"
        ],
        "stores": [
          {
            "path": "SAFETY STOCK",
            "name": "Safety Stock",
            "desc": "수요 변동 대비 안전재고 계산"
          },
          {
            "path": "REORDER POINT",
            "name": "재고 충족률",
            "desc": "4주 수요 대비 현재 재고 비율"
          },
          {
            "path": "ACTION",
            "name": "액션 추천",
            "desc": "재고보충 · 프로모션 조정 우선순위"
          }
        ]
      },
      {
        "code": "RPT",
        "en": "FORECAST REPORT",
        "title": "4주 발주 리포트",
        "shape": "품목별 재고 · 수요 · 권장발주",
        "bullets": [
          "현재재고 · 예측수요(4주) · 권장발주 · 상태"
        ],
        "table": CATALOG.products.map((p) => {
          const f = FORECAST_BY_ID[p.product_id];
          return {
            stage: p.product_name,
            shape: `재고 ${f.stock} → 수요 ${f.demand_4w} · 발주 ${f.order} · ${f.status}`,
          };
        })
      },
      {
        "code": "OUT",
        "en": "OUTPUT · 산출물",
        "title": "재고 실행안 4종",
        "shape": "피처 · 모델 · 예측 · 액션",
        "bullets": [
          "출력: 재고 운영 액션 추천 API"
        ],
        "grid": [
          {
            "n": "OUT 01",
            "name": "모델 입력 데이터셋",
            "desc": "정규화된 피처셋"
          },
          {
            "n": "OUT 02",
            "name": "선형 기준선",
            "desc": "선형 추세 · 잔차 기반 구간 설정"
          },
          {
            "n": "OUT 03",
            "name": "확률적 예측 결과",
            "desc": "p10 / p50 / p90 예측 구간"
          },
          {
            "n": "OUT 04",
            "name": "재고 운영 액션추천",
            "desc": "Safety Stock · Reorder · 액션"
          }
        ]
      },
      {
        "code": "SUM",
        "en": "SUMMARY",
        "title": "예측 정확도 개선 목표",
        "shape": "입력 → 판매 이력 → 선형 추세 → 구간 → 발주",
        "bullets": [
          "시나리오 예측 · 재고 리스크 최소화",
          "다음: 과제1 패브릭 · 과제5 PMF Score"
        ],
        "metricLabel": "기획 목표 · 백테스트 미실시",
        "metric": "70% → 90% (목표)",
        "table": [
          {
            "stage": "입력",
            "shape": "시계열 판매 테이블 + 이벤트 스트림"
          },
          {
            "stage": "Layer1 · 피처",
            "shape": "정규화된 피처셋"
          },
          {
            "stage": "Layer2 · 선형 기준선",
            "shape": "선형 추세 · 잔차 기반 구간 설정"
          },
          {
            "stage": "Layer3 · 예측",
            "shape": "p10 / p50 / p90 예측 구간"
          },
          {
            "stage": "Layer4 · 재고",
            "shape": "Safety Stock · Reorder Point · 액션"
          },
          {
            "stage": "출력",
            "shape": "재고 운영 액션 추천 API"
          }
        ]
      }
    ]
  },
  "anomaly": {
    "id": "anomaly",
    "title": "Anomaly Detection",
    "subtitle": "과제 4 · Real-time Response",
    "steps": [
      {
        "code": "ID",
        "en": "ANOMALY DETECTION",
        "title": "이상탐지 엔진",
        "shape": "과제 4 · 실시간 대응",
        "hero": true,
        "bullets": [
          "역할 — 스트림 → 이상 점수",
          "방법 — 베이스라인 · 잔차 · z-score",
          "산출 — 이상 알림 · 원인 후보",
          "연계 — 패브릭 운영·광고 로그"
        ],
        "jsonTitle": "모델 정의",
        "json": "{\n  \"model\": \"Anomaly Detection\",\n  \"task\": 4,\n  \"role\": \"스트림 → 이상 점수\",\n  \"output\": [\"anomaly_score\", \"alert\"]\n}"
      },
      {
        "code": "IN",
        "en": "INPUT · 실시간 스트림",
        "title": "모의 로그 · 카탈로그 4종",
        "shape": "스트리밍 로그 산포",
        "bullets": [
          "판매·광고 — 주문 · CTR · ROAS · CVR",
          "재고·가격 — 현재고 · 판매가",
          "비교 기준 — 같은 카탈로그 4 SKU"
        ],
        "jsonTitle": "입력 스케치",
        "json": J({ ts: `${CATALOG.as_of}T10:24:15Z`, product_id: P1.product_id, sku: P1.sku, orders: P1.ad_metrics.purchase, stock: P1.stock, ctr: P1.ad_metrics.ctr, roas: P1.ad_metrics.roas, cvr: P1.behavior.cvr })
      },
      {
        "code": "L1",
        "en": "LAYER 1 · 비교 기준선",
        "title": "카탈로그 평균과 표준편차",
        "shape": "6개 지표의 표준화 거리",
        "bullets": [
          "카탈로그 4종의 6개 지표를 z-score로 표준화",
          "평균 제곱 z-score로 상품별 거리 계산",
          "AutoEncoder 학습은 연결하지 않음"
        ],
        "jsonTitle": "Baseline 모델 메타",
        "json": J({ model: N1.model, baseline_as_of: CATALOG.as_of, distance_threshold: N1.threshold, features: ['orders', 'stock', 'ctr', 'roas', 'cvr', 'price'] })
      },
      {
        "code": "L2",
        "en": "LAYER 2 · 실시간 이상 탐지",
        "title": "모의 지표의 이상 점수",
        "shape": "정상 클러스터 밖 이상치",
        "bullets": [
          "입력: 고정된 카탈로그의 모의 지표",
          "판정: 평균 제곱 z-score와 데모 임계값 비교",
          "상태: 정상 · 주의 · 이상"
        ],
        "grid": N1.causes.map((row) => ({
          n: `${Math.round(row.contribution * 100)}%`,
          name: CAUSE_KO[row.factor] || row.factor,
          desc: `${N1.state} · 이상 원인 비중`,
        }))
      },
      {
        "code": "L3",
        "en": "LAYER 3 · 원인 분석",
        "title": "이상 점수의 지표별 비중",
        "shape": "원인 기여도 순위",
        "bullets": [
          "제곱 z-score 비중을 원인 후보로 제시",
          "후보: 주문 · 재고 · CTR · ROAS · 전환 · 가격",
          "산출: 상위 4개 지표 · 인과관계 검증은 별도"
        ],
        "jsonTitle": "원인 진단 출력",
        "json": J({ product_id: N1.product_id, sku: N1.sku, anomaly_score: N1.anomaly_score, state: N1.state, top_causes: N1.causes })
      },
      {
        "code": "L4",
        "en": "LAYER 4 · 진단 리포트 연계",
        "title": "알림 선행 · 리포트 연계",
        "shape": "Gemini · Slack/SMS · 대시보드",
        "bullets": [
          "알림·리포트 연계 형식 예시 · 외부 전송 없음"
        ],
        "stores": [
          {
            "path": "GEMINI API",
            "name": "LLM 리포트 생성",
            "desc": "리포트 초안 연계 예시"
          },
          {
            "path": "SLACK / SMS",
            "name": "알림 채널 전송",
            "desc": "Slack · Email · SMS · Kakao 연계 설계"
          },
          {
            "path": "DASHBOARD",
            "name": "대시보드 연계",
            "desc": "실시간 운영 현황 모니터링"
          }
        ]
      },
      {
        "code": "ALT",
        "en": "ALERT LOG",
        "title": "이상 탐지 알림 예시",
        "shape": "시각 · 유형 · 대응 · 채널 · 담당",
        "bullets": [
          "감지시간 · 이상유형 · 원인 · 자동대응 · 채널 · 담당"
        ],
        "table": CATALOG.products.slice(0, 3).map((p, i) => {
          const n = ANOMALY_BY_ID[p.product_id];
          const f = FORECAST_BY_ID[p.product_id];
          const channels = ['Slack · 박지훈(운영)', 'Email · 김수진(마케팅)', 'SMS · 최호진(운영)'];
          return {
            stage: `${p.product_name} · ${n.state} ${n.anomaly_score}`,
            shape: `${CAUSE_KO[n.causes[0].factor] || n.causes[0].factor} → ${f.action} · ${channels[i]}`,
          };
        })
      },
      {
        "code": "OUT",
        "en": "OUTPUT · 산출물",
        "title": "이상 신호 → 알림 4종",
        "shape": "Baseline · Score · 원인 · 채널",
        "bullets": [
          "출력 예시: 알림 데이터 + 운영 대시보드 연계"
        ],
        "grid": [
          {
            "n": "OUT 01",
            "name": "정상 기준선 모델",
            "desc": "카탈로그 z-score 기준선"
          },
          {
            "n": "OUT 02",
            "name": "이상탐지 결과",
            "desc": "Anomaly Score + 상태"
          },
          {
            "n": "OUT 03",
            "name": "원인진단 리포트",
            "desc": "제곱 z-score 비중 JSON"
          },
          {
            "n": "OUT 04",
            "name": "알림·대시보드 연계",
            "desc": "Slack · SMS · 대시보드"
          }
        ]
      },
      {
        "code": "SUM",
        "en": "SUMMARY",
        "title": "실시간 대응을 위한 연계 설계",
        "shape": "입력 → 기준선 → 탐지 → 원인 후보 → 알림 예시",
        "bullets": [
          "이상 징후 선행 포착",
          "다음: 과제5 PMF Score 알림 연계"
        ],
        "metricLabel": "대응 주기 목표 (실측 전)",
        "metric": "1일1회 → 실시간",
        "table": [
          {
            "stage": "입력",
            "shape": "모의 로그 4종 · 처리량 목표 2,000건/일+"
          },
          {
            "stage": "Layer1 · 비교 기준선",
            "shape": "Baseline Model"
          },
          {
            "stage": "Layer2 · 탐지",
            "shape": "Anomaly Score + 상태"
          },
          {
            "stage": "Layer3 · 원인",
            "shape": "제곱 z-score 비중 + 원인 후보"
          },
          {
            "stage": "Layer4 · 연계",
            "shape": "알림 · 대시보드 연계 예시"
          },
          {
            "stage": "출력",
            "shape": "알림 데이터 + 운영 대시보드 연계"
          }
        ]
      }
    ]
  },
  "pmf": {
    "id": "pmf",
    "title": "Multimodal PMF",
    "subtitle": "과제 5 · Scoring Model",
    "steps": [
      {
        "code": "ID",
        "en": "MULTIMODAL PMF",
        "title": "멀티모달 PMF",
        "shape": "과제 5 · 시장 적합성 점수",
        "hero": true,
        "bullets": [
          "역할 — XAI · 수요예측 · 이상탐지 · 시장성과 합산",
          "산출 — PMF 0~100 · 모델별 기여 · 강함/양호/주의/약함",
          "의미 — 모의 입력을 고정 가중치로 비교하는 예시",
          "설명 — 점수 = 가중 합 · 근거 JSON 동봉"
        ],
        "jsonTitle": "모델 정의",
        "json": J({ model: S1.model, task: 5, role: '모델 합산 → 시장 적합성', formula: S1.formula, output: ['pmf_score', 'verdict', 'parts', 'reasons'] })
      },
      {
        "code": "IN",
        "en": "INPUT · 4모델",
        "title": "합산 입력 · 과제 2~4 + 시장성과",
        "shape": "XAI · 수요 · 이상 · 광고/행동",
        "bullets": [
          "시장 성과 — ROAS · CTR · 재구매 · 전환",
          "XAI — 리뷰 속성별 감성",
          "수요예측 — p10 / p50 / p90 · 재고 상태",
          "이상탐지 — anomaly score · 원인"
        ],
        "jsonTitle": "합산 입력",
        "json": J({ product_id: P1.product_id, market: P1.ad_metrics, xai: { absa_mean: A1.absa_mean }, forecast: { p50: F1.p50, status: F1.status }, anomaly: { score: N1.anomaly_score, state: N1.state } })
      },
      {
        "code": "S1",
        "en": "STAGE 1 · 모델 점수",
        "title": "모델별 0~100 환산",
        "shape": "시장 · XAI · 수요 · 운영",
        "bullets": [
          "각 모델 출력을 0~100으로 정규화",
          "시장 성과 — 모의 광고·행동 지표",
          "운영 안정 — 100 − 이상점수 (높을수록 안정)"
        ],
        "jsonTitle": "모델별 점수",
        "json": J({ product_id: S1.product_id, channel_scores: S1.channel_scores })
      },
      {
        "code": "S2",
        "en": "STAGE 2 · 가중합",
        "title": "설명가능 융합",
        "shape": "가중치 × 점수 → 기여분",
        "bullets": [
          "고정 가중: 시장 0.30 · XAI 0.30 · 수요 0.25 · 운영 0.15",
          "기여분 = 가중치 × 모델점수",
          "PMF = 기여분 합 (0~100)"
        ],
        "jsonTitle": "융합식",
        "json": J({ product_id: S1.product_id, formula: S1.formula, parts: S1.parts.map((p) => ({ label: p.label, score: p.score, weight: p.weight, contribution: p.contribution })), pmf_score: S1.pmf_score })
      },
      {
        "code": "S3",
        "en": "STAGE 3 · 기여 분해",
        "title": "왜 이 점수인가",
        "shape": "모델별 기여 · 근거",
        "bullets": [
          "큰 기여분은 점수와 가중치의 곱이 큼을 뜻함",
          "약점은 가중치와 별개로 채널 점수가 가장 낮은 항목",
          "근거는 각 모델 evidence에 남김"
        ],
        "jsonTitle": "기여 분해",
        "json": J({ product_id: S1.product_id, parts: S1.parts.map((p) => ({ label: p.label, contribution: p.contribution, evidence: p.evidence })), reasons: S1.reasons })
      },
      {
        "code": "S4",
        "en": "STAGE 4 · 시장 적합성",
        "title": "최종 PMF Score",
        "shape": "0~100 · 강함/양호/주의/약함",
        "bullets": [
          "강함 75+ · 양호 60+ · 주의 45+ · 약함 45 미만",
          "판정은 데모 기준 · 실제 PMF 검증은 별도",
          "상품 클릭 시 기여·근거 카드"
        ],
        "jsonTitle": "최종 PMF Score",
        "json": J({ product_id: S1.product_id, sku: S1.sku, pmf_score: S1.pmf_score, verdict: S1.verdict, meaning: S1.meaning, formula: S1.formula, reasons: S1.reasons }),
        "metricLabel": "PMF · 시장 적합성",
        "metric": `${S1.pmf_score} · ${S1.verdict}`
      },
      {
        "code": "QTY",
        "en": "RANKING",
        "title": "상품별 시장 적합성",
        "shape": "PMF 순위",
        "bullets": [
          "같은 식으로 4상품 비교",
          "낮은 점수 = 약한 모델이 드러남"
        ],
        "table": PMF_SCORES.map((s, i) => ({
          stage: `#${i + 1} ${s.product_name}`,
          shape: `${s.pmf_score} · ${s.verdict} · 약점 ${s.weakest.label} ${s.weakest.score}`,
        }))
      },
      {
        "code": "OUT",
        "en": "OUTPUT · 산출물",
        "title": "PMF 산출물 4종",
        "shape": "점수 · XAI · 채널분해 · 검증로그",
        "bullets": [
          "추후 검증: 카테고리 보정 · 교차검증 · 전문가 검토"
        ],
        "grid": [
          {
            "n": "OUT 01",
            "name": "PMF Score",
            "desc": "시장 적합성 0~100"
          },
          {
            "n": "OUT 02",
            "name": "모델 기여 리포트",
            "desc": "왜 이 점수인가"
          },
          {
            "n": "OUT 03",
            "name": "약점 근거",
            "desc": "깎는 모델 + evidence"
          },
          {
            "n": "OUT 04",
            "name": "판정",
            "desc": "강함 · 양호 · 주의 · 약함"
          }
        ]
      },
      {
        "code": "SUM",
        "en": "SUMMARY",
        "title": "모델 합산 · 시장에서 먹히는가",
        "shape": "과제2~4 + 시장성과 → PMF",
        "bullets": [
          "입력: XAI · 수요예측 · 이상탐지 · 광고/행동",
          "출력: 설명가능한 시장 적합성 점수"
        ],
        "metricLabel": "PMF · 시장 적합성",
        "metric": `${S1.pmf_score} · ${S1.verdict}`,
        "table": [
          { stage: "시장 성과", shape: `${S1.channel_scores.market}점 × 0.30` },
          { stage: "XAI 고객 반응", shape: `${S1.channel_scores.xai}점 × 0.30` },
          { stage: "수요 전망", shape: `${S1.channel_scores.forecast}점 × 0.25` },
          { stage: "운영 안정", shape: `${S1.channel_scores.anomaly}점 × 0.15` },
          { stage: "PMF", shape: `${S1.pmf_score} · ${S1.verdict}` },
          { stage: "의미", shape: S1.meaning },
        ]
      }
    ]
  }
};
